/**
 * Cliente HTTP do exame de assinatura eletrônica (E74.F1, ADR-0075) — raia isolada.
 *
 * Espelha apps/api/types/exame_eletronico.py e apps/api/runtime/exame_eletronico.py.
 * O escritório transcreve o protocolo da plataforma de assinatura por CCB, salva
 * (PUT) e manda verificar (POST): o servidor recomputa o código de verificação
 * (SHA-256) da peça, confronta com o declarado, confere identidade e IDs, monta a
 * linha do tempo e devolve o quadro. Nada aqui conhece a triagem visual nem o LR.
 */
import axios, { AxiosError, type AxiosInstance } from 'axios';
import type {
  AssinaturaDigitalApuradaData,
  VereditoAssinaturaDigital,
} from '@/lib/exame_eletronico_assinatura_digital';
import type { Tendencia } from '@/lib/pre_analise';

export type { Modalidade } from '@/lib/cases';
export type {
  AssinaturaDigitalApuradaData,
  AssinaturaEmbutidaData,
  CertificadoSignatarioData,
  ChaveAssinaturaDigital,
  VereditoAssinaturaDigital,
} from '@/lib/exame_eletronico_assinatura_digital';

export type {
  BiometriaProtocoloData,
  CodigoDeclaradoInput,
  CoordenadaProtocoloData,
  DispositivoProtocoloData,
  EventoProtocoloData,
  FonteCoordenada,
  IpProtocoloData,
  PapelIp,
  ProtocoloAssinaturaCasoData,
  ProtocoloAssinaturaData,
  SignatarioProtocoloData,
  TipoAssinatura,
  TipoEventoProtocolo,
} from '@/lib/exame_eletronico_protocolo';
export {
  FONTES_COORDENADA,
  PAPEIS_IP,
  TIPOS_ASSINATURA,
  TIPOS_EVENTO,
  protocoloDoCaso,
} from '@/lib/exame_eletronico_protocolo';

import type {
  CodigoDeclaradoInput,
  FonteCoordenada,
  PapelIp,
  ProtocoloAssinaturaCasoData,
  ProtocoloAssinaturaData,
  TipoEventoProtocolo,
} from '@/lib/exame_eletronico_protocolo';

// Contrato — saída (quadro do exame) ---------------------------------------

export type SituacaoItem = 'confere' | 'nao_confere' | 'ausente' | 'nao_executado';
export type SeveridadeFlag = 'alta' | 'media';
export type GrauConvergencia = 'forte' | 'medio' | 'fraco' | 'inconclusivo' | 'inconsistente';
export type ExameEletronicoStatus = 'rascunho' | 'verificado' | 'concluido';
export type TipoIp = 'residencial' | 'movel' | 'datacenter' | 'vpn_proxy' | 'desconhecido';
export type QualidadeCoordenada = 'confiavel' | 'limitada' | 'ausente';
export type VinculoCluster =
  | 'titular'
  | 'conjuge'
  | 'ascendente'
  | 'descendente'
  | 'colateral'
  | 'trabalho'
  | 'outro';
export type FaixaDistancia =
  | 'ate_100m'
  | 'ate_500m'
  | 'ate_2km'
  | 'ate_10km'
  | 'acima_10km'
  | 'indeterminada';

/** Chaves estáveis dos itens do quadro — a UI rotula por chave. */
export type ChaveIntegridade = 'hash_ccb' | 'custodia_interna';
export type ChaveIdentidade =
  | 'cpf_signatario'
  | 'nome_signatario'
  | 'envelope_id'
  | 'assinatura_id'
  | 'sessao_biometrica'
  | 'tipo_assinatura';
/** Estágios que dependem de provedor externo (E74.F2). */
export type ChaveEnriquecimento = 'cluster_cadastral' | 'geocodificacao' | 'classificacao_ip';
export interface ItemVerificacaoData {
  chave: string;
  situacao: SituacaoItem;
  detalhe?: string | null;
  esperado?: string | null;
  obtido?: string | null;
}

export interface RedFlagData {
  codigo: string;
  severidade: SeveridadeFlag;
  detalhe?: string | null;
}

export interface EventoLinhaDoTempoData {
  tipo: TipoEventoProtocolo;
  em_utc?: string | null;
  em_local?: string | null;
  fora_de_ordem: boolean;
  utc_local_incoerentes: boolean;
}

export interface ClassificacaoIpData {
  endereco: string;
  papel: PapelIp;
  asn?: string | null;
  provedor?: string | null;
  tipo: TipoIp;
  situacao: SituacaoItem;
  detalhe?: string | null;
  /** Consulta externa que sustenta a classificação (elo achado → evidência). */
  consulta_id?: string | null;
}

export interface EnderecoClusterData {
  pessoa: string;
  vinculo: VinculoCluster;
  endereco: string;
  fonte: string;
  tipo_endereco?: string | null;
  /** Data em que a fonte valia — a temporalidade que o laudo precisa declarar. */
  data_da_fonte?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  precisao_geocodificacao?: string | null;
  distancia_m?: number | null;
  faixa: FaixaDistancia;
  consulta_id?: string | null;
  consulta_geo_id?: string | null;
}

export interface CoordenadaAvaliadaData {
  latitude: number;
  longitude: number;
  fonte: FonteCoordenada;
  precisao_m?: number | null;
  qualidade: QualidadeCoordenada;
  em?: string | null;
}

export interface ResultadoExameEletronicoData {
  integridade: ItemVerificacaoData[];
  identidade: ItemVerificacaoData[];
  linha_do_tempo: EventoLinhaDoTempoData[];
  sequencia_ilogica: boolean;
  /** Situação de cada estágio com provedor externo (E74.F2). */
  enriquecimento: ItemVerificacaoData[];
  ips: ClassificacaoIpData[];
  cluster: EnderecoClusterData[];
  coordenada: CoordenadaAvaliadaData | null;
  coordenada_situacao: SituacaoItem;
  /**
   * E74.F4 — `null` é o exame verificado ANTES desta Fase existir. Distingue
   * "não havia esse estágio" de "o estágio rodou e não achou assinatura".
   */
  assinatura_digital?: AssinaturaDigitalApuradaData | null;
  /**
   * E76.F4 (ADR-0076) — veredito subordinado do estágio, calculado no backend.
   * `null`/ausente é o exame anterior ao veredito: a UI não renderiza alegação.
   */
  assinatura_digital_veredito?: VereditoAssinaturaDigital | null;
  red_flags: RedFlagData[];
  ausencias: string[];
  grau_convergencia: GrauConvergencia;
  parametros_versao: string;
  executado_em: string;
}

/** Uma reabertura da conclusão: quem tinha assinado, com que grau e por quê caiu. */
export interface ReaberturaRegistroData {
  reaberto_em: string;
  reaberto_por: string | null;
  motivo: string;
  grau_anterior: GrauConvergencia | null;
  concluido_em: string | null;
  concluido_por: string | null;
}

export interface ExameEletronicoRecordData {
  id: string;
  case_id: string;
  source_document_id: string;
  dados_protocolo: ProtocoloAssinaturaData;
  resultado: ResultadoExameEletronicoData | null;
  grau_convergencia: GrauConvergencia | null;
  tendencia: Tendencia | null;
  status: ExameEletronicoStatus;
  parametros_versao: string | null;
  grau_justificativa?: string | null;
  conclusao_texto?: string | null;
  observacoes_perito?: string | null;
  verificado_em: string | null;
  concluido_em: string | null;
  concluido_por?: string | null;
  historico_reaberturas?: ReaberturaRegistroData[];
  created_by?: string | null;
  created_at: string;
  updated_at: string;
}

/** Item da lista `GET /exame-eletronico`: uma entrada por CCB do caso. */
/** Resposta do `GET /exame-eletronico` desde a E78.F2: protocolo + peças. */
export interface ExameEletronicoCasoData {
  protocolo: ProtocoloAssinaturaCasoData | null;
  pecas: ExameEletronicoCcbData[];
}

export interface ExameEletronicoCcbData {
  source_document_id: string;
  ccb_number: string | null;
  /** E78.F1 — nome do arquivo enviado; identifica a peça sem número de CCB. */
  original_filename: string;
  documento_content_sha256: string | null;
  /** E78.F2 — o que a peça declara; existe antes de qualquer verificação. */
  codigo_declarado?: string | null;
  observacoes_peca?: string | null;
  /**
   * Redação padrão do grau, já preenchida com os dados desta CCB — uma entrada
   * por grau. Vem no payload (e não por uma rota consultada a cada troca) porque
   * a mesa deixa o perito reclassificar; vazio enquanto o exame não rodou.
   */
  conclusoes_sugeridas?: Partial<Record<GrauConvergencia, string>>;
  exame: ExameEletronicoRecordData | null;
}

// Erros tipados -----------------------------------------------------------

export type ExameEletronicoErrorReason =
  | 'case_not_found'
  | 'document_not_found'
  | 'not_found'
  | 'modalidade_invalida'
  | 'sem_protocolo'
  | 'nao_verificado'
  | 'justificativa_requerida'
  | 'concluido'
  | 'nao_concluido'
  | 'certificacao_requerida'
  | 'protocolo_peca_concluida'
  | 'protocolo_nao_importado'
  | 'documento_ilegivel'
  | 'documento_sem_texto'
  | 'arquivo_nao_suportado'
  | 'arquivo_vazio'
  | 'arquivo_muito_grande'
  | 'arquivo_nao_e_imagem'
  | 'conferencia_sem_par'
  | 'foto_integridade'
  | 'foto_indisponivel'
  | 'conferencia_indisponivel'
  | 'provider_auth'
  | 'provider_credit'
  | 'sem_chave'
  | 'validation'
  | 'forbidden'
  | 'network'
  | 'unknown';

export class ExameEletronicoError extends Error {
  public readonly reason: ExameEletronicoErrorReason;

  constructor(reason: ExameEletronicoErrorReason) {
    super(`exame_eletronico_error:${reason}`);
    this.name = 'ExameEletronicoError';
    this.reason = reason;
  }
}

const HTTP_FORBIDDEN = 403;
const HTTP_NOT_FOUND = 404;
const HTTP_UNPROCESSABLE = 422;

const _DETAIL_TO_REASON: Record<string, ExameEletronicoErrorReason> = {
  CASE_NOT_FOUND: 'case_not_found',
  SOURCE_DOCUMENT_NOT_FOUND: 'document_not_found',
  EXAME_ELETRONICO_NOT_FOUND: 'not_found',
  CASE_MODALIDADE_INVALIDA: 'modalidade_invalida',
  EXAME_ELETRONICO_SEM_PROTOCOLO: 'sem_protocolo',
  EXAME_ELETRONICO_NAO_VERIFICADO: 'nao_verificado',
  EXAME_ELETRONICO_CONCLUIDO: 'concluido',
  EXAME_ELETRONICO_NAO_CONCLUIDO: 'nao_concluido',
  EXAME_ELETRONICO_JUSTIFICATIVA_REQUERIDA: 'justificativa_requerida',
  EXAME_ELETRONICO_CERTIFICACAO_REQUERIDA: 'certificacao_requerida',
  PROTOCOLO_CASO_PECA_CONCLUIDA: 'protocolo_peca_concluida',
  // E77.F1 — recusas da importação do protocolo. Cada uma tem uma frase própria
  // na tela: o que o perito faz a seguir é diferente em cada caso.
  PROTOCOLO_NAO_IMPORTADO: 'protocolo_nao_importado',
  DOCUMENT_UNREADABLE: 'documento_ilegivel',
  DOCUMENT_SEM_TEXTO: 'documento_sem_texto',
  UNSUPPORTED_MIME: 'arquivo_nao_suportado',
  FILE_EMPTY: 'arquivo_vazio',
  FILE_TOO_LARGE: 'arquivo_muito_grande',
  // E80.F1 — recusas do anexo de foto. A extensão e o MIME são do cliente; só
  // abrir os bytes diz se aquilo é uma fotografia (F-E80.F1-02).
  FOTO_IMAGE_EXTENSION_INVALID: 'arquivo_nao_suportado',
  FOTO_IMAGE_MIME_INVALID: 'arquivo_nao_suportado',
  FOTO_IMAGE_EMPTY: 'arquivo_vazio',
  FOTO_IMAGE_TOO_LARGE: 'arquivo_muito_grande',
  FOTO_IMAGE_INVALIDA: 'arquivo_nao_e_imagem',
  FOTO_NAO_ENCONTRADA: 'not_found',
  // E80.F2 — a conferência facial. A recusa da CONTA do provedor tem código
  // próprio porque a ação de quem opera é diferente em cada caso, e
  // "não foi possível" não diria nenhuma das duas (E70.F2).
  CONFERENCIA_SEM_PAR_ELEITO: 'conferencia_sem_par',
  FOTO_INTEGRIDADE_DIVERGENTE: 'foto_integridade',
  // E80.F3.T07 (F-E80.F2-08): ler e conferir são coisas diferentes. Falha de
  // leitura pede nova tentativa; adulteração pede material novo.
  FOTO_INDISPONIVEL: 'foto_indisponivel',
  CONFERENCIA_INDISPONIVEL: 'conferencia_indisponivel',
  CONFERENCIA_PROVIDER_AUTH: 'provider_auth',
  CONFERENCIA_PROVIDER_CREDIT: 'provider_credit',
  USER_HAS_NO_API_KEY: 'sem_chave',
};

function reasonForStatus(status: number): ExameEletronicoErrorReason {
  if (status === HTTP_FORBIDDEN) return 'forbidden';
  if (status === HTTP_NOT_FOUND) return 'not_found';
  // 422 sem `detail` de domínio = validação de corpo (ex.: hash fora do formato).
  if (status === HTTP_UNPROCESSABLE) return 'validation';
  return 'unknown';
}

export function classifyExameEletronicoError(error: unknown): ExameEletronicoError {
  if (!axios.isAxiosError(error)) return new ExameEletronicoError('unknown');
  const axErr = error as AxiosError<{ detail?: unknown }>;
  if (axErr.response === undefined) return new ExameEletronicoError('network');
  const detail = axErr.response.data?.detail;
  // Dois erros da mesa levam corpo ESTRUTURADO em vez de código seco (os dois
  // graus da reclassificação; quando a certificação foi pedida). Ler só a forma
  // string deixaria a tela cair em "erro inesperado" justamente nos dois casos
  // em que ela tem algo específico a dizer.
  const codigo =
    typeof detail === 'string'
      ? detail
      : typeof detail === 'object' && detail !== null && 'code' in detail
        ? String((detail as { code: unknown }).code)
        : undefined;
  const mapped = codigo === undefined ? undefined : _DETAIL_TO_REASON[codigo];
  if (mapped !== undefined) return new ExameEletronicoError(mapped);
  return new ExameEletronicoError(reasonForStatus(axErr.response.status));
}

// Rotas -------------------------------------------------------------------

function basePath(caseId: string): string {
  return `/casos/${caseId}/exame-eletronico`;
}

export async function listExamesEletronicos(
  client: AxiosInstance,
  caseId: string,
): Promise<ExameEletronicoCasoData> {
  try {
    const { data } = await client.get<ExameEletronicoCasoData>(basePath(caseId));
    return data;
  } catch (err) {
    throw classifyExameEletronicoError(err);
  }
}

/**
 * Grava/substitui o protocolo do CASO (E78.F2).
 *
 * Recusado com `protocolo_peca_concluida` quando alguma peça já tem conclusão
 * do perito: o protocolo alimenta todas elas (ADR-0077 §2.4).
 */
export async function salvarProtocoloDoCaso(
  client: AxiosInstance,
  caseId: string,
  protocolo: ProtocoloAssinaturaCasoData,
): Promise<ProtocoloAssinaturaCasoData> {
  try {
    const { data } = await client.put<ProtocoloAssinaturaCasoData>(
      `${basePath(caseId)}/protocolo`,
      protocolo,
    );
    return data;
  } catch (err) {
    throw classifyExameEletronicoError(err);
  }
}

/** Grava o código declarado e as observações da peça; invalida a verificação dela. */
export async function salvarCodigoDaPeca(
  client: AxiosInstance,
  caseId: string,
  sourceDocumentId: string,
  payload: CodigoDeclaradoInput,
): Promise<ExameEletronicoRecordData> {
  try {
    const { data } = await client.put<ExameEletronicoRecordData>(
      `${basePath(caseId)}/${sourceDocumentId}/codigo`,
      payload,
    );
    return data;
  } catch (err) {
    throw classifyExameEletronicoError(err);
  }
}

/**
 * Roda o motor sobre as N peças vivas do caso; peça concluída é pulada.
 *
 * `atualizarConsultas` vale para o CASO: a consulta paga é refeita uma vez, não
 * uma por peça (ADR-0077 §2.5).
 */
export async function verificarCaso(
  client: AxiosInstance,
  caseId: string,
  options: { atualizarConsultas?: boolean } = {},
): Promise<ExameEletronicoRecordData[]> {
  try {
    const { data } = await client.post<ExameEletronicoRecordData[]>(
      `${basePath(caseId)}/verificar`,
      { atualizar_consultas: options.atualizarConsultas ?? false },
    );
    return data;
  } catch (err) {
    throw classifyExameEletronicoError(err);
  }
}

/**
 * Roda a verificação determinística e devolve o exame com o quadro.
 *
 * `atualizarConsultas` refaz as consultas às fontes externas — é a única ação
 * que gasta consulta paga. Sem ela, o servidor reusa o que já consultou neste
 * caso, e duas verificações do mesmo protocolo devolvem o mesmo quadro.
 */
export async function verificarExame(
  client: AxiosInstance,
  caseId: string,
  sourceDocumentId: string,
  options: { atualizarConsultas?: boolean } = {},
): Promise<ExameEletronicoRecordData> {
  try {
    const { data } = await client.post<ExameEletronicoRecordData>(
      `${basePath(caseId)}/${sourceDocumentId}/verificar`,
      { atualizar_consultas: options.atualizarConsultas ?? false },
    );
    return data;
  } catch (err) {
    throw classifyExameEletronicoError(err);
  }
}

/** Corpo do `POST /concluir` — a decisão do perito sobre a CCB. */
export interface ConclusaoEletronicaInput {
  grau: GrauConvergencia;
  grau_justificativa?: string | null;
  conclusao_texto: string;
  observacoes_perito?: string | null;
}

/**
 * Fixa a decisão do perito e fecha o exame da CCB.
 *
 * Reclassificar o grau apurado exige justificativa: o servidor recusa com
 * `justificativa_requerida`, e a régua é dele — a tela desabilita o botão antes
 * por conveniência, não como garantia.
 */
export async function concluirExame(
  client: AxiosInstance,
  caseId: string,
  sourceDocumentId: string,
  conclusao: ConclusaoEletronicaInput,
): Promise<ExameEletronicoRecordData> {
  try {
    const { data } = await client.post<ExameEletronicoRecordData>(
      `${basePath(caseId)}/${sourceDocumentId}/concluir`,
      conclusao,
    );
    return data;
  } catch (err) {
    throw classifyExameEletronicoError(err);
  }
}

/**
 * Desfaz a conclusão da CCB, guardando no histórico o que caiu.
 *
 * Recusado depois que a certificação daquela CCB foi requerida
 * (`certificacao_requerida`): ali o cliente já recebeu o resultado.
 */
export async function reabrirExame(
  client: AxiosInstance,
  caseId: string,
  sourceDocumentId: string,
  motivo: string,
): Promise<ExameEletronicoRecordData> {
  try {
    const { data } = await client.post<ExameEletronicoRecordData>(
      `${basePath(caseId)}/${sourceDocumentId}/reabrir`,
      { motivo },
    );
    return data;
  } catch (err) {
    throw classifyExameEletronicoError(err);
  }
}
